// src/pages/BienvenuePage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Écran 6 — « Studio Transmission » (campagne The Collection After You, plan §3).
//
// Où l'utilisateur atterrit en cliquant le lien magique reçu à l'écran 5 (quiz sur
// vision.artkiv.com). Le compte a déjà été créé par signInWithOtp ; handle_new_user
// a préconfiguré le profil (archétype, indice) et accordé l'essai 14 j SANS carte
// (free_until). Cet écran ne fait donc que LIRE et présenter — aucune écriture
// sensible côté client. Sa seule mission : donner la première action, jamais un
// dashboard vide.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

// Présentation des archétypes — emoji/couleur repris de la Carte de Transmission.
const ARCHETYPE_UI = {
  batisseur: { emoji: "📈", color: "#2563eb" },
  passionne: { emoji: "❤️", color: "#dc2626" },
  connecteur: { emoji: "🤝", color: "#7c3aed" },
  gardien: { emoji: "🏛️", color: "#0f766e" },
  mecene: { emoji: "✨", color: "#c9a961" },
};

function formatDate(value, locale) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(locale || "fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return null;
  }
}

export default function BienvenuePage() {
  const { t, i18n } = useTranslation();
  const { user, profile, loading, isAuthenticated } = useAuth();
  // Laisse à l'échange du lien magique le temps de s'établir avant de conclure
  // « non connecté » : sur cross-domaine, la session peut mettre un instant.
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setWaited(true), 2500);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    document.title = t("bienvenue.meta.title");
  }, [t]);

  if (loading || (!isAuthenticated && !waited)) {
    return (
      <Shell>
        <p style={{ color: "#6b6459" }}>{t("bienvenue.connecting")}</p>
      </Shell>
    );
  }

  // Lien expiré / ouvert sur un autre appareil : on ne bloque pas, on réoriente.
  if (!isAuthenticated) {
    return (
      <Shell>
        <h1 style={h1Style}>{t("bienvenue.expired.title")}</h1>
        <p style={pStyle}>{t("bienvenue.expired.body")}</p>
        <Link to="/login" style={ctaPrimary}>{t("bienvenue.expired.cta")}</Link>
      </Shell>
    );
  }

  const meta = user?.user_metadata || {};
  const key = profile?.archetype || meta.archetype || null;
  const ui = (key && ARCHETYPE_UI[key]) || null;
  const affinity = profile?.quiz_affinity ?? meta.quiz_affinity ?? null;
  const trialEnd = formatDate(profile?.free_until, i18n.language);
  const firstName = profile?.first_name || "";
  // Compte déjà existant (ex. quiz refait avec l'e-mail d'un compte ancien) : on ne lui
  // annonce ni nouvel essai ni « première étape », il retrouve simplement son profil.
  const createdAt = profile?.created_at ? new Date(profile.created_at).getTime() : null;
  const isReturning = createdAt != null && Date.now() - createdAt > 2 * 24 * 3600 * 1000;
  const ns = isReturning ? "bienvenue.returning" : "bienvenue";

  return (
    <Shell>
      <p style={eyebrow}>{t(`${ns}.eyebrow`)}</p>
      <h1 style={h1Style}>
        {firstName ? t(`${ns}.hello`, { name: firstName }) : t(`${ns}.helloNoName`)}
      </h1>

      {ui && (
        <div style={archBadge}>
          <span style={{ ...medallion, background: `${ui.color}1a` }}>{ui.emoji}</span>
          <span>
            <span style={{ display: "block", fontSize: "0.72rem", color: "#8a8378" }}>
              {t("bienvenue.yourProfile")}
            </span>
            <span style={{ fontWeight: 700, color: ui.color, fontSize: "1.15rem" }}>
              {t(`bienvenue.archetype.${key}`)}
              {affinity != null ? ` · ${affinity}%` : ""}
            </span>
          </span>
        </div>
      )}

      {isReturning ? (
        <div style={missionBox}>
          <p style={{ ...missionText, fontSize: "0.9rem", color: "#4a453d" }}>{t("bienvenue.returning.note")}</p>
          <p style={missionText}>{t("bienvenue.returning.text")}</p>
          <Link to="/my-collection" style={ctaPrimary}>{t("bienvenue.returning.cta")}</Link>
        </div>
      ) : (
        <>
          {/* Confirmation de l'essai — sans carte, ce qui est la promesse tenue. */}
          <div style={trialBox}>
            <strong>{t("bienvenue.trial.title")}</strong>
            <p style={{ margin: "0.4rem 0 0", fontSize: "0.9rem", color: "#4a453d" }}>
              {trialEnd
                ? t("bienvenue.trial.until", { date: trialEnd })
                : t("bienvenue.trial.generic")}
              {" "}
              {t("bienvenue.trial.noCard")}
            </p>
          </div>

          {/* La première mission — jamais un dashboard vide (plan §3). */}
          <div style={missionBox}>
            <p style={missionLabel}>{t("bienvenue.mission.label")}</p>
            <p style={missionText}>{t("bienvenue.mission.text")}</p>
            <Link to="/my-collection" style={ctaPrimary}>{t("bienvenue.mission.cta")}</Link>
          </div>

          <Link to="/my-collection" style={secondaryLink}>{t("bienvenue.skip")}</Link>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#f9f7f4,#f1ede6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2.5rem 1.25rem", fontFamily: "Georgia, serif" }}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e0d8", borderRadius: 18, padding: "2.5rem" }}>
        {children}
      </div>
    </div>
  );
}

const eyebrow = { fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a8378", margin: "0 0 0.75rem" };
const h1Style = { fontSize: "1.8rem", fontWeight: 700, lineHeight: 1.2, color: "#1a1a1a", margin: "0 0 1.5rem" };
const pStyle = { fontSize: "0.95rem", lineHeight: 1.6, color: "#4a453d", margin: "0 0 1.5rem" };
const archBadge = { display: "flex", alignItems: "center", gap: "0.9rem", padding: "1rem", background: "#f9f7f4", border: "1px solid #e5e0d8", borderRadius: 12, marginBottom: "1.25rem" };
const medallion = { width: 52, height: 52, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", flexShrink: 0 };
const trialBox = { background: "#eef5f1", border: "1px solid #cfe3d8", borderRadius: 12, padding: "1rem 1.15rem", marginBottom: "1.25rem", color: "#2e7d55" };
const missionBox = { background: "#fff", border: "1px solid #e5e0d8", borderRadius: 12, padding: "1.5rem", marginBottom: "1.25rem" };
const missionLabel = { fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "#c9a961", margin: "0 0 0.6rem" };
const missionText = { fontSize: "1.05rem", lineHeight: 1.5, color: "#1a1a1a", margin: "0 0 1.25rem" };
const ctaPrimary = { display: "inline-block", background: "#1a1a1a", color: "#f9f7f4", textDecoration: "none", padding: "0.85rem 1.75rem", borderRadius: 10, fontSize: "0.95rem" };
const secondaryLink = { display: "inline-block", color: "#8a8378", fontSize: "0.85rem", textDecoration: "underline" };
